import {
  AutocompleteController,
  DatePickerController,
  GroupAncestors,
  GroupCodesController,
  HtmlEditorController,
  SwitchController,
  TextFieldController,
  ToggleButtonGroupController,
} from '@/components/controls';
import { ConfirmationDialog } from '@/components/dialogs';
import { isNotTooBig, startCase } from '@/utils';
import {
  objectiveForm,
  occurrenceNumberOnObjectives,
  useRestricted,
} from '@/utils/config';
import {
  Button,
  Chip,
  Divider,
  FormControl,
  FormGroup,
  FormLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, startOfDay } from 'date-fns';
import { Fragment, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ApplicableToControllers } from './ApplicableToControllers';
import { BoundaryViewer } from './BoundaryViewer';
import { LocationPicker } from './LocationPicker';
import { WeeklySchedulePickerController } from './WeeklySchedulePickerController';

export function ObjectiveForm({
  defaultValues,
  canEdit,
  onSubmit,
  onDelete,
  objectiveAttributes,
  emailAddresses,
  activeDirectoryGroups,
  typeOptions,
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const {
    control,
    formState: { dirtyFields, isSubmitting },
    getValues,
    handleSubmit,
    reset,
    setValue,
  } = useForm({ defaultValues });
  const isDirty = Object.keys(dirtyFields).length > 0;
  const [
    created,
    lastEdit,
    visibleTo,
    endTime,
    startTime,
    resourceType,
    notificationsOnEntry,
    notificationsOnExit,
    boundaryType,
    boundarySubtype,
    boundaryIdentifier,
    objectiveId,
  ] = useWatch({
    control,
    name: [
      'created',
      'lastEdit',
      'visibleTo',
      'endTime',
      'startTime',
      'resourceType',
      'notifications.onEntry',
      'notifications.onExit',
      'boundaryType',
      'boundarySubtype',
      'boundaryIdentifier',
      '_id',
    ],
  });

  function isEditable(fieldName) {
    return (
      canEdit && (objectiveForm[fieldName]?.editable || !defaultValues?._id)
    );
  }

  function isDisabled(fieldName) {
    if (fieldName === 'endTime') {
      return !isEditable(fieldName) && defaultValues?.endTime < new Date();
    }

    return !isEditable(fieldName);
  }

  function handleResourceTypeChange() {
    setValue('personCodes', undefined);
    setValue('vehicleIdentificationNumbers', undefined);
    setValue('personGroupCodes', undefined);
    setValue('vehicleGroupCodes', undefined);
  }

  function handleBoundaryTypeChange() {
    setValue('boundary', null);
    setValue('microbeats', []);
    setValue('boundaryIdentifier', '');
    setValue('boundarySubtype', '');
  }

  function handleCancel() {
    reset();
  }

  function toggleDeleteDialog() {
    setDeleteOpen((prev) => !prev);
  }

  function handleDelete() {
    onDelete(defaultValues._id);
  }

  return (
    <Fragment>
      <form id="objective-form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={1}>
          <Stack
            spacing={1}
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="h6" sx={{ p: 1 }}>
              {defaultValues?._id ? defaultValues.title : 'New Objective'}
            </Typography>
            {defaultValues?._id && (
              <Tooltip
                title={
                  <table>
                    <tbody>
                      {created && (
                        <tr>
                          <td>
                            {`Created by ${getValues('created').userId} ${format(
                              getValues('created').time,
                              'dd/MM/yyyy HH:mm',
                            )}`}
                          </td>
                        </tr>
                      )}
                      {lastEdit && (
                        <tr>
                          <td>
                            {`Last edited by ${getValues('lastEdit').userId} ${format(
                              getValues('lastEdit').time,
                              'dd/MM/yyyy HH:mm',
                            )}`}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                }
              >
                <Chip size="small" label={`#${defaultValues._id}`} />
              </Tooltip>
            )}
          </Stack>
          <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap' }}>
            <TextFieldController
              name="title"
              label="Title"
              control={control}
              disabled={isDisabled('title')}
              rules={{ required: 'Required', maxLength: 50 }}
            />
            {occurrenceNumberOnObjectives && (
              <TextFieldController
                name="occurrenceNumber"
                label="Occurrence Number"
                control={control}
                disabled={isDisabled('occurenceNumber')}
                rules={{ maxLength: 30 }}
              />
            )}
            {useRestricted && (
              <SwitchController
                name="visibleTo"
                label="Restricted"
                control={control}
                disabled={isDisabled('visibleTo')}
                parse={(value) => !!value}
                format={(value) => (value ? [] : undefined)}
              />
            )}
          </Stack>
          {visibleTo && (
            <Fragment>
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  Visible to
                </Typography>
              </Divider>
              <AutocompleteController
                name="visibleTo"
                control={control}
                fullWidth
                options={activeDirectoryGroups}
                disabled={isDisabled('visibleTo')}
              />
            </Fragment>
          )}
          <GroupCodesController
            name="groupCodes"
            control={control}
            label="Groups & Areas"
            disabled={isDisabled('groupCodes')}
          />
          {defaultValues?.groupAncestors?.length > 0 && (
            <Fragment>
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  Ancestor Groups & Areas
                </Typography>
              </Divider>
              <GroupAncestors
                groupAncestors={defaultValues?.groupAncestors}
                typeOptions={typeOptions}
              />
            </Fragment>
          )}
          {objectiveAttributes.length > 0 && (
            <Fragment>
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  Attributes
                </Typography>
              </Divider>
              <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap' }}>
                {objectiveAttributes.map(({ value, label, values }) => (
                  <TextFieldController
                    key={value}
                    name={`attributes.${value}`}
                    label={label}
                    control={control}
                    disabled={!canEdit}
                    options={values}
                  />
                ))}
              </Stack>
            </Fragment>
          )}
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Content
            </Typography>
          </Divider>
          <HtmlEditorController
            name="contentHTML"
            placeholder="Write something..."
            control={control}
            disabled={isDisabled('contentHTML')}
            rules={{ required: 'Required', validate: isNotTooBig }}
          />
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Active Time
            </Typography>
          </Divider>
          <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap' }}>
            <DatePickerController
              name="startTime"
              label="Start Date"
              control={control}
              disabled={isDisabled('startTime')}
              rules={{ required: 'Required' }}
              minDate={startOfDay(new Date())}
              maxDate={endTime ?? new Date('2100-01-01')}
            />
            <DatePickerController
              name="endTime"
              label="End Date"
              control={control}
              disabled={isDisabled('endTime')}
              rules={{ required: 'Required' }}
              minDate={startTime ?? startOfDay(new Date())}
              maxDate={new Date('2100-01-01')}
            />
          </Stack>
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Weekly Schedule
            </Typography>
          </Divider>
          <WeeklySchedulePickerController
            name="schedule"
            control={control}
            disabled={isDisabled('schedule')}
            rules={{ required: 'Required' }}
          />
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Requirements
            </Typography>
          </Divider>
          <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap' }}>
            <TextFieldController
              name="requiredVisits"
              label="Number of Visits"
              control={control}
              disabled={isDisabled('requiredVisits')}
              rules={{ required: 'Required' }}
              parse={(value) => (value == null || value === '' ? '' : value)}
              format={(value) => (value === '' ? '' : +value)}
            />
            <TextFieldController
              name="requiredFrequency"
              label="Frequency"
              control={control}
              options={['total', 'daily', 'hourly'].map((item) => ({
                label: startCase(item),
                value: item,
              }))}
              disabled={isDisabled('requiredFrequency')}
              rules={{ required: 'Required' }}
            />
            <Divider orientation="vertical" flexItem />
            <TextFieldController
              name="complianceSeconds"
              label="Compliant Minutes"
              control={control}
              disabled={isDisabled('complianceSeconds')}
              rules={{ required: 'Required' }}
              parse={(value) =>
                value == null || value === '' ? '' : value / 60
              }
              format={(value) => (value === '' ? '' : value * 60)}
            />
          </Stack>
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Applicable to
            </Typography>
          </Divider>
          <Stack spacing={1}>
            <ToggleButtonGroupController
              name="resourceType"
              control={control}
              rules={{ required: 'Required' }}
              disabled={isDisabled('resourceType')}
              values={[
                { label: 'People', value: 'people' },
                { label: 'Vehicles', value: 'vehicles' },
                { label: 'Groups of People', value: 'personGroups' },
                { label: 'Groups of Vehicles', value: 'vehicleGroups' },
              ]}
              onChange={handleResourceTypeChange}
            />
            <ApplicableToControllers
              control={control}
              resourceType={resourceType}
              isDisabled={isDisabled}
            />
          </Stack>
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Notifications
            </Typography>
          </Divider>
          <Stack spacing={1}>
            <FormControl
              sx={{ pl: 1.5 }}
              component="fieldset"
              variant="standard"
            >
              <FormLabel component="legend">
                <Typography variant="caption">Type</Typography>
              </FormLabel>
              <FormGroup row>
                <SwitchController
                  name="notifications.onEntry"
                  label="Entry"
                  control={control}
                />
                <SwitchController
                  name="notifications.onExit"
                  label="Exit"
                  control={control}
                />
              </FormGroup>
            </FormControl>
            {(notificationsOnEntry || notificationsOnExit) && (
              <AutocompleteController
                name="notifications.recipients"
                control={control}
                label="Recipients"
                fullWidth
                options={emailAddresses}
                disabled={!canEdit}
              />
            )}
          </Stack>
          <Divider>
            <Typography variant="caption" color="textSecondary">
              Boundary
            </Typography>
          </Divider>
          <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap' }}>
            <TextFieldController
              name="boundaryType"
              control={control}
              label="Type"
              options={['Location', 'Microbeats', 'Custom']}
              disabled={isDisabled('boundary')}
              rules={{ required: 'Required' }}
              onChange={handleBoundaryTypeChange}
            />
            {boundaryType === 'Location' && (
              <LocationPicker
                control={control}
                subtype={boundarySubtype}
                setValue={setValue}
                disabled={isDisabled('boundary')}
              />
            )}
          </Stack>
          <BoundaryViewer
            control={control}
            type={boundaryType}
            id={boundaryIdentifier}
            disabled={isDisabled('boundary')}
          />
          {canEdit && (
            <Stack spacing={1} direction="row">
              <Button
                color="primary"
                type="submit"
                disabled={!isDirty || isSubmitting}
              >
                Save
              </Button>
              <Button
                color="primary"
                disabled={!isDirty || isSubmitting}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              {canEdit && (
                <Button
                  color="error"
                  onClick={toggleDeleteDialog}
                  disabled={!objectiveId}
                >
                  Delete
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </form>
      <ConfirmationDialog
        action="Delete"
        open={deleteOpen}
        itemId={defaultValues?.title || defaultValues?.identifier}
        onOk={handleDelete}
        onCancel={toggleDeleteDialog}
      />
    </Fragment>
  );
}
